import { api } from '@koridor/api-client';

type MatchmakingFoundPayload = {
  matchId?: string;
  opponent?: { id?: string; nickname?: string; rating?: number };
};

type RoomJoinedPayload = {
  matchId?: string;
  boardSize?: number;
  ranked?: boolean;
};

const matchmakingListeners = new Set<(payload: MatchmakingFoundPayload) => void>();
const roomJoinedListeners = new Set<(payload: RoomJoinedPayload) => void>();
let wired = false;

/** Replay window for subscribers that mount after WS delivery (lazy routes, StrictMode). */
const SIGNAL_REPLAY_MS = 60_000;
let lastMatchmakingFound: { payload: MatchmakingFoundPayload; at: number } | null = null;
let lastRoomJoined: { payload: RoomJoinedPayload; at: number } | null = null;

export function onMatchmakingFound(handler: (payload: MatchmakingFoundPayload) => void) {
  matchmakingListeners.add(handler);
  if (lastMatchmakingFound && Date.now() - lastMatchmakingFound.at < SIGNAL_REPLAY_MS) {
    queueMicrotask(() => handler(lastMatchmakingFound!.payload));
  }
  return () => { matchmakingListeners.delete(handler); };
}

export function onRoomJoined(handler: (payload: RoomJoinedPayload) => void) {
  roomJoinedListeners.add(handler);
  if (lastRoomJoined && Date.now() - lastRoomJoined.at < SIGNAL_REPLAY_MS) {
    queueMicrotask(() => handler(lastRoomJoined!.payload));
  }
  return () => { roomJoinedListeners.delete(handler); };
}

/** Clear buffered matchmaking event after successful entry (tests / explicit reset). */
export function clearMatchmakingFoundBuffer() {
  lastMatchmakingFound = null;
}

/** Clear buffered room.joined before a new lobby session. */
export function clearRoomJoinedBuffer() {
  lastRoomJoined = null;
}

/** @internal resets module state between unit tests */
export function __resetOnlineMatchSignalsForTests() {
  wired = false;
  lastMatchmakingFound = null;
  lastRoomJoined = null;
  matchmakingListeners.clear();
  roomJoinedListeners.clear();
}

/** Keep WS listeners alive across screen transitions so events are not missed during lazy route loads. */
export function ensureOnlineMatchSignals() {
  if (wired) return () => {};
  wired = true;

  const offMm = api.on('matchmaking.found', (payload: unknown) => {
    const p = payload as MatchmakingFoundPayload;
    lastMatchmakingFound = { payload: p, at: Date.now() };
    matchmakingListeners.forEach((fn) => fn(p));
  });
  const offRoom = api.on('room.joined', (payload: unknown) => {
    const p = payload as RoomJoinedPayload;
    lastRoomJoined = { payload: p, at: Date.now() };
    roomJoinedListeners.forEach((fn) => fn(p));
  });

  return () => {
    offMm();
    offRoom();
    wired = false;
    lastMatchmakingFound = null;
    lastRoomJoined = null;
  };
}
